import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { AuthTokens, LoginCredentials, RegisterData } from '@/types'
import { authService } from '@/services/authService'
import { useAppStore } from '@/stores/app.store'
import { User, UserModel } from '@/models/user.model'

interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: { user: User; tokens: AuthTokens } } //dont use yet
  | { type: 'REGISTER_FAILURE'; payload: string }                             //dont use yet
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean }

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'REGISTER_FAILURE':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: action.payload ? true : false,
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    default:
      return state
  }
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  clearError: () => void
  refreshToken: () => Promise<void>
  updateUser: (user: User | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const {
    user: storedUser,
    setUser: setStoreUser,
    setTokens,
    accessToken,
    refreshToken: refreshTokenVal,
    expiresIn,
  } = useAppStore((state) => state)

  const syncUser = (nextUser: User | null) => {
    const normalizedUser = nextUser ? UserModel.fromJSON(nextUser) : null
    setStoreUser(normalizedUser)
    dispatch({ type: 'SET_USER', payload: normalizedUser })
  }

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // const storedTokens = await authService.getStoredTokens()
        const storedTokens = { accessToken: accessToken ?? '', refreshToken: refreshTokenVal ?? '', expiresIn: expiresIn ?? 0 }
        // console.log('Stored Tokens:', storedTokens)
        // console.log('Stored User:', storedUser)
        if (refreshTokenVal && accessToken && storedUser) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user: storedUser, tokens: storedTokens },
          })
          // TODO: figure out why this endpoint throws 401 even with valid token
          // Try to get current user from API
          // const currentUser = await authService.getCurrentUser()
          // dispatch({
          //   type: 'LOGIN_SUCCESS',
          //   payload: { user: currentUser, tokens: storedTokens },
          // })
        } else {
          dispatch({ type: 'LOGOUT' })
        }
      } catch (error) {
        // Token is invalid, clear it
        authService.clearStoredTokens()
        dispatch({ type: 'LOGOUT' })
        console.error('Error initializing auth:', error)
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    initializeAuth()
  }, [storedUser, accessToken, refreshTokenVal, expiresIn])

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const {
        data: { user: loginUser, tokens },
        success,
        message,
      } = await authService.login(credentials)
      if (success) {
        const normalizedUser = UserModel.fromJSON(loginUser)
        authService.storeTokens(tokens)
        setTokens(tokens)
        syncUser(normalizedUser)
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: normalizedUser, tokens },
        })
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: message || 'Login failed' })
        throw new Error(message || 'Login failed')
      }
    } catch (error: any) {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: error.message || 'Login failed',
      })
      throw error
    }
  }


  const register = async (data: RegisterData) => {
    try {
      // Call your backend
      await authService.register(data)

      // Don’t log the user in automatically (optional) —
      // instead redirect them to login page after success
    } catch (error: any) {
      dispatch({
        type: 'REGISTER_FAILURE',
        payload: error.message || 'Registration failed',
      })
      throw error
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const logout = () => {
    authService.clearStoredTokens()
    syncUser(null)
    setTokens(null)
    dispatch({ type: 'LOGOUT' })
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const refreshToken = async () => {
    try {
      const tokens = await authService.refreshToken()
      authService.storeTokens(tokens)
      setTokens(tokens)
      const user = await authService.getCurrentUser()
      const normalizedUser = UserModel.fromJSON(user)
      syncUser(normalizedUser)
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: normalizedUser, tokens },
      })
    } catch (error) {
      dispatch({ type: 'LOGOUT' })
    }
  }

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    refreshToken,
    updateUser: syncUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
